import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DEFAULT_FEATURES, PLATFORM_FEATURES, WORKSPACE_BASE, money, quoteFeaturePlan } from '../utils/planQuote.js';
import {
  IconArrowRight, IconChat, IconCheck, IconCheckCircle, IconContacts, IconLightning, IconPhone, IconSend, IconWhatsApp,
} from '../components/icons/Icons.jsx';
import { BrandMark, Eyebrow, SectionTitle, SiteFooter, SiteHeader } from '../components/marketing/SiteChrome.jsx';
import { appUrl } from '../utils/publicUrls.js';
import { paymentsApi } from '../api/payments.api.js';
import { formattedPreviewGrandTotal, formattedPreviewTotal, loadPaddleSdk, paddleCheckoutItems, previewPaddleItems, previewPaddlePrice } from '../utils/paddleCheckout.js';
import { BLOG_LINKS, SERVICE_LINKS } from '../data/marketingContent.js';

const useCases = ['Events', 'Retail', 'Med spas', 'Agencies', 'Churches', 'Local service'];

const featureBlocks = [
  ['Reach every opted-in customer fast', 'Import CSVs, segment by tags, preview SMS length, schedule campaigns, and track delivery.', IconSend],
  ['Connect WhatsApp without extra tabs', 'Send through Direct WhatsApp API, sync replies, and forward conversations to your own endpoint.', IconWhatsApp],
  ['Never miss another customer call', 'Use browser or mobile calling, inbound routing, IVR menus, call logs, and fallback forwarding.', IconPhone],
  ['Let AI follow up automatically', 'Build keyword replies, webhook intake, reminders, digests, and multi-step workflows from plain English.', IconLightning],
];

const trustStats = [
  ['Private', 'workspace per business'],
  ['STOP-safe', 'opt-outs suppressed'],
  ['Mobile', 'inbox and calls ready'],
  ['7 days', 'trial included'],
];

const setupSteps = [
  ['Choose your channels', 'Select SMS, WhatsApp, calls, email, automation, or only the features your business needs.'],
  ['Create the workspace', 'Add your business name, owner email, password, and the account opens with the right access.'],
  ['Connect payment securely', 'Checkout stores the payment method and starts the included trial before the first charge.'],
  ['Manage everything yourself', 'Change features, billing, setup, team users, and exports from your own account.'],
];

const seoUseCases = [
  ['Twilio bulk SMS campaigns', 'Send customer lists through Twilio-backed SMS, manage replies, tags, delivery status, and STOP handling.'],
  ['Twilio missed call text back', 'Send an automatic SMS when a customer calls and nobody answers, then keep the callback in the CRM.'],
  ['Keyword SMS automation', 'Trigger replies when someone texts DANCE, INFO, BOOK, HELP, or any keyword you choose.'],
  ['WhatsApp CRM messaging', 'Connect WhatsApp messaging with customer history so conversations do not sit in a separate tab.'],
  ['IVR and call routing', 'Create keypad menus for inbound calls and send follow-up messages based on the option selected.'],
  ['Email campaign tracking', 'Send email offers, track opens, clicks, bounces, replies, and keep results beside each contact.'],
];

const faqs = [
  ['Can I use this with Twilio?', 'Yes. Ineedlead CRM is built for Twilio SMS, Twilio voice, inbound webhooks, missed call text back, call routing, and delivery status tracking.'],
  ['Can I pay only for one feature?', 'Yes. You can choose only Bulk SMS, only WhatsApp, only Email, Calls, Automation, or any combination that fits the business.'],
  ['Does every customer get a private workspace?', 'Yes. Each business has its own workspace, users, credentials, customer list, messages, calls, automations, and billing access.'],
  ['Can the platform handle STOP and unsubscribe replies?', 'Yes. STOP replies are marked as opted out and skipped automatically in future SMS campaigns.'],
  ['Can I create missed call callbacks?', 'Yes. Calls can be logged, routed, forwarded, or followed up with an automatic text back based on your setup.'],
  ['Can AI build automations?', 'Yes. The AI assistant can help create keyword replies, webhook intake, contact tagging, reminders, digests, and IVR-style flows.'],
  ['Can I export my contacts and conversations?', 'Yes. Contacts, opt-outs, conversations, campaigns, and records can be exported from the workspace.'],
  ['Is there a mobile app?', 'Yes. The web app is mobile-ready, and Android app download access can be shown from Settings when enabled.'],
];


function scrollToPricing() {
  document.getElementById('builder')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const inputClass = 'w-full min-h-[44px] rounded-[10px] border border-mk-line bg-white px-3 py-2.5 text-sm text-mk-ink outline-none transition placeholder:text-mk-muted/70 hover:border-mk-strong focus:border-mk-primary focus:ring-[3px] focus:ring-mk-primary/20';
const primaryBtn = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] bg-mk-primary px-5 text-sm font-bold text-white shadow-[0_5px_14px_rgba(85,87,232,.2)] transition hover:-translate-y-px hover:bg-mk-hover hover:shadow-[0_7px_18px_rgba(85,87,232,.26)] disabled:translate-y-0 disabled:opacity-50';
const secondaryBtn = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] border border-mk-line bg-white px-5 text-sm font-semibold text-mk-ink transition hover:border-mk-strong hover:bg-mk-bg';
const cardClass = 'rounded-[14px] border border-mk-line bg-white shadow-[0_1px_2px_rgba(15,23,42,.03),0_10px_30px_rgba(31,42,68,.05)]';

function Field({ label, children }) {
  return (
    <label className="grid gap-2 text-sm font-semibold text-mk-ink">
      {label}
      {children}
    </label>
  );
}

function CheckBullet({ children }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm text-mk-muted">
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-mk-successSoft text-mk-success">
        <IconCheck size={12} />
      </span>
      {children}
    </span>
  );
}

function IconTile({ icon: Icon, size = 20 }) {
  return (
    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[11px] bg-mk-soft text-mk-primary">
      <Icon size={size} />
    </span>
  );
}

function FeatureCard({ feature, selected, period, price, onToggle, onStart, featured = false }) {
  return (
    <div
      className={`relative flex min-h-[400px] flex-col p-6 text-left transition hover:-translate-y-1 ${cardClass} ${
        selected ? '!border-mk-primary ring-[3px] ring-mk-primary/15' : 'hover:border-mk-strong'
      }`}
    >
      {featured && (
        <span className="absolute -top-3 left-6 rounded-full bg-mk-primary px-3 py-1 text-[11px] font-bold uppercase tracking-[.06em] text-white shadow-[0_5px_14px_rgba(85,87,232,.25)]">
          Popular add-on
        </span>
      )}
      <button type="button" onClick={onToggle} className="block w-full text-left">
        <div className="flex items-start justify-between gap-4">
          <p className="text-lg font-bold leading-snug text-mk-ink">{feature.label}</p>
          <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border transition ${selected ? 'border-mk-primary bg-mk-primary text-white' : 'border-mk-strong bg-white'}`}>
            {selected && <IconCheck size={13} />}
          </span>
        </div>
        <p className="mt-3 min-h-[72px] text-sm leading-relaxed text-mk-muted">{feature.summary}</p>
      </button>
      <div className="mt-auto">
        <div className="border-t border-mk-line pt-5">
          <p className="text-4xl font-bold tracking-tight text-mk-ink tabular-nums">{price || money(period === 'year' ? feature.yearlyCents : feature.monthlyCents)}</p>
          <p className="mt-1 text-sm text-mk-muted">{period === 'year' ? 'per year after trial' : 'per month after trial'}</p>
        </div>
        <div className="mt-5 grid gap-2">
          <button type="button" onClick={onStart} className={primaryBtn}>
            Get started
          </button>
          <button type="button" onClick={onToggle} className={`${secondaryBtn} ${selected ? '!border-mk-primary/40 !bg-mk-soft !text-mk-softText' : ''}`}>
            {selected ? <><IconCheck size={14} /> Added to plan</> : 'Add to plan'}
          </button>
        </div>
        <p className="mt-4 text-xs text-mk-muted">Secure checkout · 7-day trial included</p>
      </div>
    </div>
  );
}

function PreviewStat({ label, value, hint, icon }) {
  return (
    <div className="relative overflow-hidden rounded-[12px] border border-mk-line bg-white p-3.5">
      <span className="pointer-events-none absolute -bottom-8 -right-6 h-20 w-20 rounded-full bg-mk-soft opacity-70" />
      <div className="relative flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] text-mk-muted">{label}</p>
          <p className="mt-1.5 text-2xl font-bold leading-none text-mk-ink">{value}</p>
          {hint && <p className="mt-1.5 text-[10px] font-semibold text-mk-success">{hint}</p>}
        </div>
        <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-mk-soft text-mk-primary">{icon}</span>
      </div>
    </div>
  );
}

function ProductPreview() {
  const nav = [
    [IconChat, 'Inbox', true],
    [IconContacts, 'Contacts'],
    [IconPhone, 'Calls'],
    [IconSend, 'Campaigns'],
    [IconLightning, 'Automations'],
  ];
  const flows = [
    ['DANCE', 'Auto-reply sent', 'SMS', 'primary'],
    ['New opt-in', 'Tagged “event”', 'Flow', 'primary'],
    ['STOP', 'Suppressed from campaigns', 'Safe', 'success'],
  ];
  const pill = {
    primary: 'bg-mk-soft text-mk-softText',
    success: 'bg-mk-successSoft text-mk-success',
  };

  return (
    <div className="relative">
      <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[40px] bg-[radial-gradient(circle_at_70%_20%,rgba(85,87,232,.18),transparent_60%)]" />
      <div className="overflow-hidden rounded-[18px] border border-mk-line bg-white shadow-[0_30px_80px_rgba(31,42,68,.14)]">
        <div className="flex items-center gap-1.5 border-b border-mk-line bg-mk-subtle px-4 py-2.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          <span className="ml-3 truncate rounded-md bg-white px-3 py-1 text-[11px] text-mk-muted ring-1 ring-mk-line">app.ineedleadcrm.com/dashboard</span>
        </div>
        <div className="grid min-h-[380px] sm:grid-cols-[170px_1fr]">
          <aside className="hidden border-r border-mk-line bg-gradient-to-b from-white to-mk-subtle p-3 sm:block">
            <div className="flex items-center gap-2 border-b border-mk-line pb-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-mk-primary text-xs font-bold text-white">I</span>
              <span className="text-xs font-semibold text-mk-ink">Ineedlead CRM</span>
            </div>
            <p className="mt-3 px-2 text-[9px] font-semibold uppercase tracking-[.08em] text-mk-muted">Workspace</p>
            <div className="mt-1.5 space-y-0.5">
              {nav.map(([Icon, label, active]) => (
                <div key={label} className={`flex items-center gap-2 rounded-[8px] px-2 py-1.5 text-[11px] font-semibold ${active ? 'bg-mk-soft text-mk-softText shadow-[inset_2px_0_#5557E8]' : 'text-mk-muted'}`}>
                  <Icon size={13} /> {label}
                </div>
              ))}
            </div>
          </aside>
          <div className="bg-[radial-gradient(circle_at_92%_0%,rgba(85,87,232,.06),transparent_16rem)] bg-mk-bg p-4">
            <p className="text-[10px] font-bold uppercase tracking-[.08em] text-mk-softText">Workspace</p>
            <p className="mt-0.5 text-xl font-bold tracking-tight text-mk-ink">Dashboard</p>
            <div className="mt-3 grid grid-cols-2 gap-2.5 lg:grid-cols-3">
              <PreviewStat label="Messages sent" value="8.2K" hint="▲ 12.16%" icon={<IconSend size={14} />} />
              <PreviewStat label="Replies today" value="341" hint="▲ 4.8%" icon={<IconChat size={14} />} />
              <div className="col-span-2 lg:col-span-1">
                <PreviewStat label="Calls answered" value="96%" hint="Avg. pickup 8s" icon={<IconPhone size={14} />} />
              </div>
            </div>
            <div className="mt-3 rounded-[12px] border border-mk-line bg-white">
              <div className="flex items-center justify-between border-b border-mk-line px-3.5 py-2.5">
                <p className="text-xs font-bold text-mk-ink">Live keyword workflow</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-mk-successSoft px-2 py-0.5 text-[10px] font-bold text-mk-success">
                  <span className="h-1.5 w-1.5 rounded-full bg-mk-success" /> Live
                </span>
              </div>
              {flows.map(([title, status, type, tone]) => (
                <div key={title} className="flex items-center justify-between gap-3 border-b border-mk-line px-3.5 py-2.5 last:border-0">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-mk-ink">{title}</p>
                    <p className="truncate text-[11px] text-mk-muted">{status}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${pill[tone]}`}>{type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrustStrip() {
  return (
    <section className="px-4 sm:px-6">
      <div className="mx-auto grid max-w-7xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {trustStats.map(([value, label]) => (
          <div key={value} className={`relative overflow-hidden px-5 py-5 ${cardClass}`}>
            <span className="pointer-events-none absolute -bottom-11 -right-8 h-28 w-28 rounded-full bg-mk-soft opacity-70" />
            <p className="relative text-[28px] font-bold leading-none text-mk-ink">{value}</p>
            <p className="relative mt-2 text-sm text-mk-muted">{label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function LandingPage() {
  const [selectedFeatures, setSelectedFeatures] = useState(DEFAULT_FEATURES);
  const [billingPeriod, setBillingPeriod] = useState('month');
  const [checkoutStep, setCheckoutStep] = useState('browse');
  const [publicConfig, setPublicConfig] = useState(null);
  const [prices, setPrices] = useState({});
  const [quotePreview, setQuotePreview] = useState('');
  const [form, setForm] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    website: '',
    notes: '',
    password: '',
    confirmPassword: '',
  });
  const [notesTouched, setNotesTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const quote = useMemo(() => quoteFeaturePlan({ features: selectedFeatures, billingPeriod }), [selectedFeatures, billingPeriod]);

  const setupText = useMemo(() => {
    if (!quote.featureLabels.length) return '';
    return `I want to start with ${quote.featureLabels.join(', ')} for my business.`;
  }, [quote.featureLabels]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const selectedCheckoutItems = useMemo(() => {
    const addOns = publicConfig?.addOns || [];
    const period = billingPeriod;
    return selectedFeatures
      .map((feature) => {
        const match = addOns.find((item) => item.key === feature);
        return { feature, priceId: match?.priceId?.[period], quantity: 1 };
      })
      .filter((item) => item?.priceId);
  }, [billingPeriod, publicConfig, selectedFeatures]);

  const toggleFeature = (feature) => {
    setSelectedFeatures((current) => {
      if (current.includes(feature)) {
        const next = current.filter((item) => item !== feature);
        return next.length ? next : current;
      }
      return [...current, feature];
    });
  };

  const chooseCurrent = () => {
    setCheckoutStep('details');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToPlans = () => {
    setCheckoutStep('browse');
    setTimeout(scrollToPricing, 20);
  };

  const openCheckoutForWorkspace = async (tenant) => {
    const { data } = await paymentsApi.getConfig();
    const paddleConfig = data?.paddle;
    if (!paddleConfig?.enabled) throw new Error('Checkout is not enabled yet');
    const items = paddleCheckoutItems(paddleConfig.items);
    if (!items.length) throw new Error('Price is missing for this plan');

    const Paddle = await loadPaddleSdk({
      clientToken: paddleConfig.clientToken,
      environment: paddleConfig.environment,
      customerId: tenant?.paddleCustomerId,
      eventCallback: async (event) => {
        if (event?.name !== 'checkout.completed') return;
        const transactionId = event?.data?.transaction_id || event?.data?.transactionId || event?.data?.id;
        if (!transactionId) return;
        try {
          await paymentsApi.completePaddleCheckout(transactionId, { features: selectedFeatures, billingPeriod });
          toast.success('Payment connected. Your workspace is ready.');
          window.location.assign(appUrl('/welcome'));
        } catch (err) {
          toast.error(err.response?.data?.message || 'Payment completed, but workspace sync needs a refresh');
        }
      },
    });

    Paddle.Checkout.open({
      items,
      customer: { email: form.email },
      customData: {
        tenantId: tenant?.id,
        workspace: tenant?.name || form.businessName,
        features: selectedFeatures,
        billingPeriod,
      },
      settings: {
        displayMode: 'overlay',
        variant: 'one-page',
        successUrl: appUrl('/welcome'),
      },
    });
  };

  useEffect(() => {
    if (window.location.hash === '#builder') setTimeout(scrollToPricing, 60);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadPrices() {
      try {
        const { data } = await paymentsApi.getPublicConfig();
        if (cancelled) return;
        setPublicConfig(data?.paddle || null);
        const paddleConfig = data?.paddle;
        if (!paddleConfig?.enabled || !paddleConfig?.clientToken) return;
        const Paddle = await loadPaddleSdk({
          clientToken: paddleConfig.clientToken,
          environment: paddleConfig.environment,
        });
        const next = {};
        for (const feature of (paddleConfig.addOns || []).filter(Boolean)) {
          for (const period of ['month', 'year']) {
            const priceId = feature.priceId?.[period];
            if (!priceId) continue;
            const preview = await previewPaddlePrice({ Paddle, priceId, countryCode: paddleConfig.country });
            next[`${feature.key}:${period}`] = formattedPreviewTotal(preview);
          }
        }
        if (!cancelled) setPrices(next);
      } catch (err) {
        if (!cancelled) setPublicConfig({ error: err.message || 'Pricing is not configured yet' });
      }
    }
    loadPrices();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadQuotePreview() {
      setQuotePreview('');
      try {
        if (!publicConfig?.enabled || !publicConfig?.clientToken || !selectedCheckoutItems.length) return;
        const Paddle = await loadPaddleSdk({
          clientToken: publicConfig.clientToken,
          environment: publicConfig.environment,
        });
        const preview = await previewPaddleItems({ Paddle, items: selectedCheckoutItems, countryCode: publicConfig.country });
        if (!cancelled) setQuotePreview(formattedPreviewGrandTotal(preview));
      } catch {
        if (!cancelled) setQuotePreview('');
      }
    }
    loadQuotePreview();
    return () => {
      cancelled = true;
    };
  }, [publicConfig, selectedCheckoutItems]);

  const submit = async (event) => {
    event.preventDefault();
    if (form.businessName.trim().length < 2) return toast.error('Business name is required');
    if (form.ownerName.trim().length < 2) return toast.error('Your name is required');
    if (!form.email.includes('@')) return toast.error('Valid email is required');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    if (form.password !== form.confirmPassword) return toast.error('Passwords do not match');

    setSubmitting(true);
    try {
      const { useAuthStore } = await import('../store/authStore.js');
      const data = await useAuthStore.getState().signup({
        businessName: form.businessName,
        ownerName: form.ownerName,
        email: form.email,
        phone: form.phone,
        website: form.website,
        notes: form.notes || setupText,
        password: form.password,
        planTier: 'CUSTOM',
        features: selectedFeatures,
        billingPeriod,
      });

      toast.success('Account created. Opening secure checkout...');
      await openCheckoutForWorkspace(data?.tenant);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start checkout');
    } finally {
      setSubmitting(false);
    }
  };

  if (checkoutStep === 'details') {
    return (
      <div className="min-h-screen bg-mk-bg text-mk-ink">
        <header className="sticky top-0 z-40 border-b border-mk-line bg-white/90 px-4 backdrop-blur sm:px-6">
          <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between gap-4">
            <button type="button" onClick={backToPlans} className={`${secondaryBtn} !min-h-[40px] !px-4`}>
              ← Back to plans
            </button>
            <span className="hidden sm:block"><BrandMark /></span>
            <a href={appUrl('/login')} className={`${secondaryBtn} !min-h-[40px] !px-4`}>
              Sign in
            </a>
          </div>
        </header>

        <main className="mx-auto grid max-w-6xl gap-6 px-4 py-8 sm:px-6 sm:py-12 lg:grid-cols-[1fr_380px]">
          <form onSubmit={submit} className={`space-y-5 p-6 sm:p-8 ${cardClass}`}>
            <div>
              <Eyebrow>Step 1 of 2 · Create workspace</Eyebrow>
              <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-[-0.02em] sm:text-4xl">Your details</h1>
              <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-mk-muted">
                Create your account first. Secure checkout opens next to activate your workspace.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Business name">
                <input className={inputClass} value={form.businessName} onChange={(e) => set('businessName', e.target.value)} placeholder="Your business" />
              </Field>
              <Field label="Your name">
                <input className={inputClass} value={form.ownerName} onChange={(e) => set('ownerName', e.target.value)} placeholder="Account owner" />
              </Field>
              <Field label="Email">
                <input className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@business.com" />
              </Field>
              <Field label="Phone">
                <input className={inputClass} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1..." />
              </Field>
              <Field label="Password">
                <input type="password" className={inputClass} value={form.password} onChange={(e) => set('password', e.target.value)} placeholder="At least 8 characters" />
              </Field>
              <Field label="Confirm password">
                <input type="password" className={inputClass} value={form.confirmPassword} onChange={(e) => set('confirmPassword', e.target.value)} placeholder="Repeat password" />
              </Field>
            </div>
            <Field label="Website or domain (optional)">
              <input className={inputClass} value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://yourdomain.com" />
            </Field>
            <Field label="What do you want to set up?">
              <textarea
                className={`${inputClass} min-h-24 resize-none`}
                value={notesTouched ? form.notes : setupText}
                onChange={(e) => {
                  setNotesTouched(true);
                  set('notes', e.target.value);
                }}
                placeholder="Example: SMS campaigns for events, WhatsApp API, calls, or all modules..."
              />
            </Field>
            <button type="submit" data-intent="checkout" disabled={submitting} className={`${primaryBtn} w-full !min-h-[48px] text-[15px]`}>
              {submitting ? 'Creating account…' : 'Continue to secure payment'}
            </button>
            <p className="text-center text-xs text-mk-muted">Card details are handled securely. The first charge happens after the included trial.</p>
          </form>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className={`overflow-hidden ${cardClass}`}>
              <div className="border-b border-mk-line bg-mk-soft p-5">
                <Eyebrow>Order summary</Eyebrow>
                <h2 className="mt-1.5 text-2xl font-bold tracking-tight">Workspace setup</h2>
                <p className="mt-1 text-sm text-mk-muted">Your selected features activate after checkout.</p>
              </div>
              <div className="space-y-3 p-5">
                <div className="flex items-end justify-between rounded-[10px] bg-mk-bg p-4">
                  <div>
                    <p className="text-sm text-mk-muted">Due today</p>
                    <p className="mt-1 text-4xl font-bold tabular-nums">$0</p>
                  </div>
                  <span className="rounded-full bg-mk-successSoft px-2.5 py-1 text-xs font-bold text-mk-success">7-day trial</span>
                </div>
                <div className="rounded-[10px] border border-mk-line p-4">
                  <p className="text-sm text-mk-muted">After trial</p>
                  <p className="mt-1 text-2xl font-bold tabular-nums">{quotePreview || money(quote.totalCents)}{quote.billingPeriod === 'month' ? '/mo' : '/year'}</p>
                  <p className="mt-1 text-sm text-mk-muted">{quote.planName}</p>
                </div>
                <div className="rounded-[10px] border border-mk-line p-4">
                  <p className="text-sm text-mk-muted">Included features</p>
                  <div className="mt-3 grid gap-2">
                    {quote.featureLabels.map((label) => (
                      <CheckBullet key={label}><span className="font-semibold text-mk-ink">{label}</span></CheckBullet>
                    ))}
                  </div>
                </div>
                <button type="button" onClick={backToPlans} className={`${secondaryBtn} w-full`}>
                  Edit selected features
                </button>
              </div>
            </div>
          </aside>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mk-bg text-mk-ink">
      <div className="bg-mk-ink px-4 py-2 text-center text-[13px] text-white/80">
        Bulk SMS, WhatsApp API, calls, email and automation CRM ·{' '}
        <button type="button" onClick={scrollToPricing} className="font-semibold text-white underline-offset-4 hover:underline">Start a 7-day trial →</button>
      </div>

      <SiteHeader onGetStarted={scrollToPricing} />

      <main>
        <section className="relative overflow-hidden px-4 sm:px-6">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_0%,rgba(85,87,232,.10),transparent_34rem)]" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 py-14 lg:grid-cols-[.95fr_1.05fr] lg:py-20">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-mk-primary/15 bg-mk-soft px-3 py-1.5 text-xs font-bold uppercase tracking-[.08em] text-mk-softText">
                <span className="h-1.5 w-1.5 rounded-full bg-mk-primary" />
                Bulk SMS CRM · WhatsApp API · Calls · AI
              </span>
              <h1 className="mt-6 text-[42px] font-bold leading-[1.04] tracking-[-0.03em] text-mk-ink sm:text-[56px] xl:text-[64px]">
                Bulk SMS, WhatsApp and calls <span className="text-mk-primary">in one workspace.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-mk-muted">
                Run SMS campaigns, WhatsApp messaging, phone calls, email campaigns, short links and AI workflows from one private workspace.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={scrollToPricing} className={`${primaryBtn} !min-h-[50px] !px-7 text-[15px]`}>
                  Build my workspace <IconArrowRight size={17} />
                </button>
                <a href={appUrl('/login')} className={`${secondaryBtn} !min-h-[50px] !px-7 text-[15px]`}>
                  I already have an account
                </a>
              </div>
              <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2.5">
                <CheckBullet>7-day trial included</CheckBullet>
                <CheckBullet>Secure card checkout</CheckBullet>
                <CheckBullet>Private workspace</CheckBullet>
                <CheckBullet>Cancel anytime</CheckBullet>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-semibold uppercase tracking-[.08em] text-mk-muted">Used by</span>
                {useCases.map((item) => (
                  <span key={item} className="rounded-full border border-mk-line bg-white px-3 py-1 text-xs font-semibold text-mk-ink">{item}</span>
                ))}
              </div>
            </div>

            <ProductPreview />
          </div>
        </section>

        <TrustStrip />

        <section id="builder" className="scroll-mt-20 px-4 py-20 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-3xl text-center">
              <Eyebrow>Choose features</Eyebrow>
              <SectionTitle className="mt-3">Pay only for what you use.</SectionTitle>
              <p className="mt-4 text-lg leading-relaxed text-mk-muted">
                Select one feature or combine several. Every workspace starts with secure login and private data isolation.
              </p>
              <div className="mx-auto mt-8 inline-grid grid-cols-2 rounded-[12px] border border-mk-line bg-white p-1 shadow-sm">
                {[
                  ['month', 'Monthly'],
                  ['year', 'Yearly'],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setBillingPeriod(key)}
                    className={`min-w-[120px] rounded-[9px] px-5 py-2.5 text-sm font-semibold transition ${billingPeriod === key ? 'bg-mk-primary text-white shadow-[0_4px_12px_rgba(85,87,232,.25)]' : 'text-mk-muted hover:text-mk-ink'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-5">
              {Object.values(PLATFORM_FEATURES).map((feature) => (
                <FeatureCard
                  key={feature.key}
                  feature={feature}
                  period={billingPeriod}
                  selected={selectedFeatures.includes(feature.key)}
                  price={prices[`${feature.key}:${billingPeriod}`]}
                  onToggle={() => toggleFeature(feature.key)}
                  onStart={() => {
                    setSelectedFeatures((current) => (current.includes(feature.key) ? current : [...current, feature.key]));
                    chooseCurrent();
                  }}
                  featured={feature.key === 'AI_AUTOMATION'}
                />
              ))}
            </div>

            <div className={`mt-6 grid gap-4 p-5 lg:grid-cols-[1fr_auto] lg:items-center ${cardClass}`}>
              <div className="flex items-start gap-4">
                <IconTile icon={IconCheckCircle} />
                <div>
                  <p className="text-sm text-mk-muted">{quote.features.length} selected</p>
                  <p className="mt-0.5 text-lg font-bold text-mk-ink">{quote.featureLabels.join(', ')}</p>
                  <p className="mt-1 text-sm text-mk-muted">
                    {WORKSPACE_BASE.label} is included. {quotePreview || money(quote.totalCents)}{quote.billingPeriod === 'year' ? '/year' : '/mo'} after the included trial.
                  </p>
                  {publicConfig?.error && (
                    <p className="mt-2 text-sm font-semibold text-mk-warning">
                      Live pricing preview is not configured yet, so local USD prices are shown.
                    </p>
                  )}
                </div>
              </div>
              <button type="button" onClick={chooseCurrent} className={`${primaryBtn} !min-h-[48px] !px-8`}>
                Continue <IconArrowRight size={16} />
              </button>
            </div>
          </div>
        </section>

        <section className="border-y border-mk-line bg-white px-4 py-20 sm:px-6 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.8fr_1.2fr]">
            <div>
              <Eyebrow>Business outcomes</Eyebrow>
              <SectionTitle className="mt-3">One login for the jobs your customers expect you to do fast.</SectionTitle>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-mk-muted">
                Run broadcasts, answer replies, capture opt-ins, trigger WhatsApp follow-ups, route calls, and let AI build the workflows without spreading customer data across disconnected tools.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {featureBlocks.map(([title, text, Icon]) => (
                <div key={title} className="rounded-[14px] border border-mk-line bg-mk-subtle p-6 transition hover:-translate-y-1 hover:bg-white hover:shadow-[0_10px_30px_rgba(31,42,68,.08)]">
                  <IconTile icon={Icon} />
                  <p className="mt-5 text-lg font-bold">{title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-mk-muted">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-mk-navy px-4 py-20 text-white sm:px-6 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-12 lg:grid-cols-[.75fr_1.25fr] lg:items-center">
              <div>
                <p className="text-xs font-bold uppercase tracking-[.08em] text-[#A5A7FF]">Simple setup</p>
                <h2 className="mt-3 text-[32px] font-bold leading-[1.1] tracking-[-0.02em] sm:text-[44px]">From signup to live workspace.</h2>
                <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/65">
                  Customers choose the tools they need, create their own password, and manage payment without you manually creating every account.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {setupSteps.map(([title, text], index) => (
                  <article key={title} className="rounded-[14px] border border-white/10 bg-mk-navy2 p-6">
                    <span className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-mk-primary text-sm font-bold text-white">{index + 1}</span>
                    <h3 className="mt-5 text-lg font-bold">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/65">{text}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <Eyebrow>Solutions</Eyebrow>
              <SectionTitle className="mt-3">Built for the exact jobs customers search for.</SectionTitle>
              <p className="mt-5 text-lg leading-relaxed text-mk-muted">
                Ineedlead CRM covers the practical searches business owners make when they need Twilio bulk SMS, missed call callbacks, WhatsApp customer messaging, IVR call routing, and email campaigns.
              </p>
            </div>
            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {seoUseCases.map(([title, text]) => (
                <article key={title} className={`p-6 ${cardClass}`}>
                  <h3 className="text-lg font-bold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mk-muted">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-mk-line bg-white px-4 py-20 sm:px-6 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.8fr_1.2fr]">
            <div>
              <Eyebrow>Services</Eyebrow>
              <SectionTitle className="mt-3">Each feature has its own page.</SectionTitle>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-mk-muted">
                Read how each channel works in detail, then come back here when you are ready to choose features.
              </p>
              <Link to="/services" className={`${secondaryBtn} mt-7`}>View all services <IconArrowRight size={16} /></Link>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {SERVICE_LINKS.slice(0, 6).map((service) => (
                <Link key={service.slug} to={`/services/${service.slug}`} className="group rounded-[14px] border border-mk-line bg-mk-subtle p-5 transition hover:border-mk-primary/30 hover:bg-white hover:shadow-[0_10px_30px_rgba(31,42,68,.08)]">
                  <h3 className="flex items-center justify-between gap-3 text-base font-bold">
                    {service.title}
                    <IconArrowRight size={16} className="flex-shrink-0 text-mk-muted transition group-hover:translate-x-0.5 group-hover:text-mk-primary" />
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-mk-muted">{service.summary}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-3xl">
            <div className="text-center">
              <Eyebrow>FAQs</Eyebrow>
              <SectionTitle className="mt-3">Questions before choosing features.</SectionTitle>
            </div>
            <div className={`mt-10 divide-y divide-mk-line ${cardClass}`}>
              {faqs.map(([question, answer]) => (
                <details key={question} className="group px-6 py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-bold text-mk-ink [&::-webkit-details-marker]:hidden">
                    {question}
                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-mk-soft text-lg leading-none text-mk-primary transition group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-3 pr-10 text-sm leading-relaxed text-mk-muted">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-mk-line bg-white px-4 py-20 sm:px-6 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <Eyebrow>Blog</Eyebrow>
                <SectionTitle className="mt-3">Guides for customer messaging.</SectionTitle>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-bold text-mk-primary hover:text-mk-hover">View all posts <IconArrowRight size={15} /></Link>
            </div>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {BLOG_LINKS.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="rounded-[14px] border border-mk-line bg-mk-subtle p-6 transition hover:-translate-y-1 hover:bg-white hover:shadow-[0_10px_30px_rgba(31,42,68,.08)]">
                  <span className="rounded-full bg-mk-soft px-2.5 py-1 text-[11px] font-bold text-mk-softText">{post.category}</span>
                  <h3 className="mt-4 text-lg font-bold leading-snug">{post.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mk-muted">{post.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-20 sm:px-6">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[20px] bg-mk-primary px-6 py-14 text-center text-white sm:px-12">
            <span className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-white/10" />
            <span className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-white/5" />
            <h2 className="relative text-[32px] font-bold leading-[1.1] tracking-[-0.02em] sm:text-[44px]">Start your 7-day trial today.</h2>
            <p className="relative mx-auto mt-4 max-w-xl text-lg text-white/80">Pick your channels, create your workspace and send your first campaign in minutes.</p>
            <div className="relative mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <button type="button" onClick={scrollToPricing} className="inline-flex min-h-[50px] items-center justify-center gap-2 rounded-[10px] bg-white px-7 text-[15px] font-bold text-mk-primary transition hover:-translate-y-px hover:bg-mk-soft">
                Choose features <IconArrowRight size={17} />
              </button>
              <a href={appUrl('/login')} className="inline-flex min-h-[50px] items-center justify-center rounded-[10px] border border-white/30 px-7 text-[15px] font-semibold text-white transition hover:bg-white/10">
                Sign in
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
